import { useEffect, useState } from 'react'
import { Link, Outlet, useLocation } from 'react-router-dom'

import { useAuthProviders } from '../pages/auth/GoogleButton.tsx'

/**
 * The frame for pages you can reach without an account.
 *
 * Deliberately not `AppShell`: there is no sidebar, no mode pill, no trading state and no
 * kill switch, because none of those mean anything until there is an account behind them.
 * Showing a "Trading · PAPER" chrome to a signed-out visitor would be a lie about state.
 */

type Theme = 'dark' | 'light'

/** The marketing site. The logo leads back to it: it is where a visitor arrived from. */
const SITE_URL = 'https://www.kellystake.com'

function readTheme(): Theme {
  try {
    return (localStorage.getItem('ks-theme') as Theme) || 'dark'
  } catch {
    return 'dark'
  }
}

export function PublicShell() {
  const [theme, setTheme] = useState<Theme>(readTheme)
  const providers = useAuthProviders()
  // The waitlist is where the marketing site sends people; keep them on the form, not browsing.
  const onWaitlist = useLocation().pathname === '/waitlist'

  useEffect(() => {
    document.documentElement.setAttribute('data-theme', theme)
    try {
      localStorage.setItem('ks-theme', theme)
    } catch {
      /* private mode — the theme simply won't persist */
    }
  }, [theme])

  return (
    <>
      <header className="topbar" style={{ left: 0, position: 'sticky', padding: '0 28px' }}>
        <a className="brand" href={SITE_URL} style={{ padding: 0, color: 'var(--text)' }}>
          <span className="brand-mark">K</span>
          <span style={{ fontWeight: 750, marginLeft: 8 }}>KellyStake</span>
        </a>
        <div className="spacer" />
        {!onWaitlist && (
          <>
            <Link className="nav-item" to="/how-it-works" style={{ display: 'inline-flex' }}>
              How it works
            </Link>
            <Link className="nav-item" to="/pricing" style={{ display: 'inline-flex' }}>
              Pricing
            </Link>
          </>
        )}
        {providers?.accountsOpen && (
          <>
            <Link className="btn ghost sm" to="/login">
              Log in
            </Link>
            <Link className="btn primary sm" to="/signup">
              Start free
            </Link>
          </>
        )}
        {providers && !providers.accountsOpen && (
          <Link className="btn primary sm" to="/waitlist">
            Join
          </Link>
        )}
        <button
          className="icon-btn"
          aria-label={theme === 'dark' ? 'Switch to light theme' : 'Switch to dark theme'}
          onClick={() => setTheme(theme === 'dark' ? 'light' : 'dark')}
        >
          ◐
        </button>
      </header>

      <main
        className="content"
        style={{ marginLeft: 0, maxWidth: 1100, marginInline: 'auto', paddingTop: 36 }}
      >
        <Outlet />
      </main>

      <footer className="u-center u-faint u-sm" style={{ padding: '0 24px 48px' }}>
        KellyStake never holds your money.{' '}
        <Link to="/legal">Terms</Link> · <Link to="/legal">Privacy</Link> ·{' '}
        <Link to="/legal">Risk disclosure</Link> · 18+ · Please gamble responsibly.
      </footer>
    </>
  )
}
